import { RequireAuth, RequirePermission, type Prefetch } from '@bemor/web-kit';
import { RouteError } from '@bemor/web-kit/ui';
import type { ReactNode } from 'react';
import {
    createBrowserRouter,
    createMemoryRouter,
    redirect,
    type RouteObject,
} from 'react-router-dom';

import { AppLayout, SandboxNotice } from '@/layouts/app-layout';
import { P } from '@/lib/permissions';
import { AlertsPage } from '@/pages/alerts/index';
import { ApprovalPage } from '@/pages/approvals/detail';
import { ApprovalsPage } from '@/pages/approvals/index';
import { ApprovalRulesPage } from '@/pages/approvals/rules';
import { AuditPage } from '@/pages/audit/index';
import { InvitePage } from '@/pages/auth/invite';
import { CasePage } from '@/pages/cases/detail';
import { CasesPage } from '@/pages/cases/index';
import { ChargePage } from '@/pages/charges/detail';
import { ChargesPage } from '@/pages/charges/index';
import { DisputePage } from '@/pages/disputes/detail';
import { DisputesPage } from '@/pages/disputes/index';
import { FlagPage } from '@/pages/flags/detail';
import { FlagsPage } from '@/pages/flags/index';
import { HomePage } from '@/pages/home';
import { HttpLogsPage } from '@/pages/http-logs/index';
import { LedgerBridgePage } from '@/pages/ledger-bridge';
import { MerchantPage } from '@/pages/merchants/detail';
import { MerchantsPage } from '@/pages/merchants/index';
import { MerchantPaymentSystemsPage } from '@/pages/merchants/payment-systems';
import { RefundsPage } from '@/pages/money/refunds';
import { TaxPage } from '@/pages/money/tax';
import { NotificationLogsPage } from '@/pages/notification-logs/index';
import { NotificationsPage } from '@/pages/notifications/index';
import { OrderPage } from '@/pages/orders/detail';
import { OrdersPage } from '@/pages/orders/index';
import { OrganizationPage } from '@/pages/organizations/detail';
import { OrganizationsPage } from '@/pages/organizations/index';
import { PaymentMethodPage } from '@/pages/payment-methods/detail';
import { PaymentMethodsPage } from '@/pages/payment-methods/index';
import {
    PaymentSystemsPage,
    ProviderAccountPage,
    ProviderPage,
} from '@/pages/payment-systems/index';
import { PolicyPage } from '@/pages/policies/detail';
import { PoliciesPage } from '@/pages/policies/index';
import { BinImportsPage } from '@/pages/pricing/bin-imports';
import { CardPage } from '@/pages/pricing/card';
import { CardsPage } from '@/pages/pricing/cards';
import { ReservePoliciesPage } from '@/pages/pricing/reserve-policies';
import { ReserveSchedulePage } from '@/pages/pricing/reserve-schedule';
import { QueuePage } from '@/pages/queues/index';
import { FinancePage } from '@/pages/reports/finance';
import { MetricsPage } from '@/pages/reports/metrics';
import { StaffActivityPage } from '@/pages/reports/staff-activity';
import { RiskCasePage } from '@/pages/risk/case';
import { RiskDashboardPage } from '@/pages/risk/dashboard';
import { RiskFeedsPage } from '@/pages/risk/feeds';
import { FraudCodesPage } from '@/pages/risk/fraud-codes';
import { RiskKeyPage } from '@/pages/risk/key';
import { RiskListsPage } from '@/pages/risk/lists';
import { RiskModelsPage } from '@/pages/risk/models';
import { RiskMonitoringPage } from '@/pages/risk/monitoring';
import { RiskMonitoringDetailPage } from '@/pages/risk/monitoring-detail';
import { RiskProfilesPage } from '@/pages/risk/profiles';
import { RiskReviewsPage } from '@/pages/risk/reviews';
import { RiskRulesPage } from '@/pages/risk/rules';
import {
    ScreeningCasesPage,
    ScreeningStatusPage,
    ScreeningSubjectsPage,
    ScreeningWhitelistPage,
} from '@/pages/risk/screening';
import { ScreeningCasePage } from '@/pages/risk/screening-case';
import { SearchPage } from '@/pages/search/index';
import { SessionPage } from '@/pages/sessions/detail';
import { SessionsPage } from '@/pages/sessions/index';
import { NotificationPreferencesPage } from '@/pages/settings/notifications';
import { PushPage } from '@/pages/settings/push';
import { SecurityPage } from '@/pages/settings/security';
import { TemplatesPage } from '@/pages/settings/templates';
import { StaffPage } from '@/pages/staff/index';
import { RolesPage } from '@/pages/staff/roles';
import { StorePage } from '@/pages/stores/detail';
import { StoresPage } from '@/pages/stores/index';
import { SubscriptionPage } from '@/pages/subscriptions/detail';
import { SubscriptionsPage } from '@/pages/subscriptions/index';
import { TenantPage } from '@/pages/tenants/detail';
import { TenantsPage } from '@/pages/tenants/index';
import { TestConsolePage } from '@/pages/test-console/index';
import { UserPage } from '@/pages/users/detail';
import { UsersPage } from '@/pages/users/index';
import { layoutPrefetch, PREFETCH } from '@/prefetches';
import { CallbackPage, LoginPage } from '@bemor/web-kit/staff-auth';

const gate = (permission: string, element: ReactNode) => (
    <RequirePermission permission={permission}>{element}</RequirePermission>
);

function NotFound() {
    return (
        <div
            className="mx-auto max-w-md space-y-2 p-6 text-center"
            data-testid="not-found"
        >
            <h1 className="text-lg font-semibold">Page not found</h1>
            <p className="text-muted-foreground text-sm">
                There is nothing at this address.
            </p>
        </div>
    );
}

const page = (
    path: string,
    permission: string,
    element: ReactNode,
    prefetch?: Prefetch,
) => {
    const reads = prefetch ?? PREFETCH[path];
    return {
        // The page's reads only for someone the page lets in (the gate below): no 403 to fetch and log.
        ...(reads
            ? {
                  handle: {
                      prefetch: ((context) =>
                          context.can(permission)
                              ? reads(context)
                              : []) satisfies Prefetch,
                  },
              }
            : {}),
        path,
        element: gate(permission, element),
    };
};

export const routes: RouteObject[] = [
    { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },
    {
        path: '/auth/callback',
        element: <CallbackPage />,
        errorElement: <RouteError />,
    },
    {
        path: '/invite/:token',
        element: <InvitePage />,
        errorElement: <RouteError />,
    },
    // The link Delivery's invitation emails carry today (BM-39 2.10): the same screen. BM-45: staff have no
    // password, so there is no forgot or reset screen.
    {
        path: '/auth/invitations/accept',
        element: <InvitePage />,
        errorElement: <RouteError />,
    },
    {
        // BM-46 §2.0: the server redirects a signed-out request here instead of rendering an empty page.
        handle: { auth: true },
        element: <RequireAuth loginPath="/login" />,
        errorElement: <RouteError />,
        children: [
            {
                element: <AppLayout />,
                handle: { prefetch: layoutPrefetch },
                // BM-46 §2.9: the root's error element; a page's own error renders inside the shell (below).
                errorElement: <RouteError />,
                children: [
                    {
                        errorElement: <RouteError />,
                        children: [
                            {
                                path: '/',
                                element: <HomePage />,
                                handle: { prefetch: PREFETCH['/'] },
                            },
                            // BM-109: "Open Ledger" hands the session off to the Ledger board (any signed-in staff member)
                            { path: '/ledger', element: <LedgerBridgePage /> },
                            page('/queues/:queue', P.queuesRead, <QueuePage />),
                            page('/cases', P.casesRead, <CasesPage />),
                            page('/cases/:id', P.casesRead, <CasePage />),
                            page(
                                '/merchants',
                                P.merchantsRead,
                                <MerchantsPage />,
                            ),
                            page(
                                '/merchants/:id',
                                P.merchantsRead,
                                <MerchantPage />,
                            ),
                            // bm-111 § 2: the merchant's payment providers card, same gate as the card had
                            page(
                                '/merchants/:id/payment-systems',
                                P.systemsManage,
                                <MerchantPaymentSystemsPage />,
                            ),
                            // BM-93: the hierarchy (read with merchants.read; the lifecycle buttons check their own keys)
                            page(
                                '/organizations',
                                P.merchantsRead,
                                <OrganizationsPage />,
                            ),
                            page(
                                '/organizations/:id',
                                P.merchantsRead,
                                <OrganizationPage />,
                            ),
                            page('/stores', P.merchantsRead, <StoresPage />),
                            page('/stores/:id', P.merchantsRead, <StorePage />),
                            page('/users', P.merchantsRead, <UsersPage />),
                            page('/users/:id', P.merchantsRead, <UserPage />),
                            page('/audit', P.auditRead, <AuditPage />),
                            page('/search', P.search, <SearchPage />),
                            page('/flags', P.flagsManage, <FlagsPage />),
                            page('/flags/:key', P.flagsManage, <FlagPage />),
                            page(
                                '/approvals',
                                P.approvalsRead,
                                <ApprovalsPage />,
                            ),
                            page(
                                '/approvals/rules',
                                P.approvalsRead,
                                <ApprovalRulesPage />,
                            ),
                            page(
                                '/approvals/:id',
                                P.approvalsRead,
                                <ApprovalPage />,
                            ),
                            page('/alerts', P.alertsRead, <AlertsPage />),
                            page(
                                '/http-logs',
                                P.httpLogsRead,
                                <HttpLogsPage />,
                            ),
                            page(
                                '/http-logs/:id',
                                P.httpLogsRead,
                                <HttpLogsPage />,
                            ),
                            page(
                                '/notification-logs',
                                P.notificationsRead,
                                <NotificationLogsPage />,
                            ),
                            page(
                                '/notification-logs/:id',
                                P.notificationsRead,
                                <NotificationLogsPage />,
                            ),
                            page(
                                '/money/refunds',
                                P.chargesRead,
                                <RefundsPage />,
                            ),
                            page(
                                '/money/charges',
                                P.chargesRead,
                                <ChargesPage />,
                            ),
                            page(
                                '/money/charges/:id',
                                P.chargesRead,
                                <ChargePage />,
                            ),
                            // bm-147 § 1: every merchant's orders (tenant board only)
                            page('/orders', P.ordersRead, <OrdersPage />),
                            page('/orders/:id', P.ordersRead, <OrderPage />),
                            // bm-147 § 5: every merchant's subscriptions (tenant board only)
                            page(
                                '/subscriptions',
                                P.subscriptionsRead,
                                <SubscriptionsPage />,
                            ),
                            page(
                                '/subscriptions/:id',
                                P.subscriptionsRead,
                                <SubscriptionPage />,
                            ),
                            // bm-111 § 10: the session page lives where the charge page links a session (T-41)
                            page('/sessions', P.sessionsRead, <SessionsPage />),
                            page(
                                '/sessions/:id',
                                P.sessionsRead,
                                <SessionPage />,
                            ),
                            // bm-111 § 21: saved payment methods, where the charge page links a saved method
                            page(
                                '/payment-methods',
                                P.chargesRead,
                                <PaymentMethodsPage />,
                            ),
                            page(
                                '/payment-methods/:id',
                                P.chargesRead,
                                <PaymentMethodPage />,
                            ),
                            // BM-125 decision 20: Payment reviews became cases of Risk's one queue; the old addresses still open them
                            {
                                path: '/money/payment-reviews',
                                loader: () => redirect('/risk/reviews'),
                            },
                            page('/money/tax', P.taxRead, <TaxPage />),
                            // BM-122: the finance report and the metrics page
                            page(
                                '/reports/finance',
                                P.financeRead,
                                <FinancePage />,
                            ),
                            page(
                                '/reports/metrics',
                                P.reportsRead,
                                <MetricsPage />,
                            ),
                            // BM-136: the staff activity report (tenant board only: not in PLATFORM_PATHS)
                            page(
                                '/reports/staff-activity',
                                P.staffActivityRead,
                                <StaffActivityPage />,
                            ),
                            // BM-70 § 13: the pay page returns here with ?session=
                            page(
                                '/preview-checkout',
                                P.consoleUse,
                                <TestConsolePage />,
                            ),
                            // D-20: the old address, ?session= kept (pay pages opened before the rename return there)
                            {
                                path: '/test-console',
                                loader: ({ request }) =>
                                    redirect(
                                        `/preview-checkout${new URL(request.url).search}`,
                                    ),
                            },
                            {
                                path: '/money/payment-reviews/:id',
                                loader: ({ params }) =>
                                    redirect(
                                        `/risk/reviews/${params.id ?? ''}`,
                                    ),
                            },
                            // BM-62: the Pricing module
                            page(
                                '/pricing/cards',
                                P.pricingRead,
                                <CardsPage />,
                            ),
                            page(
                                '/pricing/cards/:id',
                                P.pricingRead,
                                <CardPage />,
                            ),
                            page(
                                '/pricing/reserve-policies',
                                P.pricingRead,
                                <ReservePoliciesPage />,
                            ),
                            // BM-125: the risk engine; slice 2 the review queue, the case and the buyer key
                            page(
                                '/risk/reviews',
                                P.riskReviewsDecide,
                                <RiskReviewsPage />,
                            ),
                            page(
                                '/risk/reviews/:id',
                                P.riskReviewsDecide,
                                <RiskCasePage />,
                            ),
                            page(
                                '/risk/keys/:kind/:key',
                                P.riskReviewsDecide,
                                <RiskKeyPage />,
                            ),
                            // BM-125 slice 3: merchant monitoring, a merchant's monitoring, the Risk dashboard
                            page(
                                '/risk/monitoring',
                                P.riskMonitoringRead,
                                <RiskMonitoringPage />,
                            ),
                            page(
                                '/risk/monitoring/:merchant',
                                P.riskMonitoringRead,
                                <RiskMonitoringDetailPage />,
                            ),
                            page(
                                '/risk/dashboard',
                                P.riskMonitoringRead,
                                <RiskDashboardPage />,
                            ),
                            page(
                                '/risk/rules',
                                P.riskRulesRead,
                                <RiskRulesPage />,
                            ),
                            page(
                                '/risk/profiles',
                                P.riskRulesRead,
                                <RiskProfilesPage />,
                            ),
                            page(
                                '/risk/lists',
                                P.riskRulesRead,
                                <RiskListsPage />,
                            ),
                            page(
                                '/risk/fraud-codes',
                                P.riskRulesRead,
                                <FraudCodesPage />,
                            ),
                            // BM-125 slice 5 (decision 45): the trained models, activated by staff
                            page(
                                '/risk/models',
                                P.riskModelsManage,
                                <RiskModelsPage />,
                            ),
                            page(
                                '/risk/feeds',
                                P.riskScreeningManage,
                                <RiskFeedsPage />,
                            ),
                            // BM-125 slice 4: sanctions screening (the tenant's cases, whitelist, subjects, index status)
                            page(
                                '/risk/screening',
                                P.riskScreeningDecide,
                                <ScreeningCasesPage />,
                            ),
                            page(
                                '/risk/screening/cases/:id',
                                P.riskScreeningDecide,
                                <ScreeningCasePage />,
                            ),
                            page(
                                '/risk/screening/whitelist',
                                P.riskScreeningDecide,
                                <ScreeningWhitelistPage />,
                            ),
                            page(
                                '/risk/screening/subjects',
                                P.riskScreeningDecide,
                                <ScreeningSubjectsPage />,
                            ),
                            page(
                                '/risk/screening/status',
                                P.riskScreeningDecide,
                                <ScreeningStatusPage />,
                            ),
                            page(
                                '/pricing/reserve-schedule',
                                P.pricingRead,
                                <ReserveSchedulePage />,
                            ),
                            page(
                                '/pricing/bin-imports',
                                P.pricingRead,
                                <BinImportsPage />,
                            ),
                            page('/disputes', P.disputesRead, <DisputesPage />),
                            page(
                                '/disputes/:id',
                                P.disputesRead,
                                <DisputePage />,
                            ),
                            page('/policies', P.auditRead, <PoliciesPage />),
                            page(
                                '/policies/:kind',
                                P.auditRead,
                                <PolicyPage />,
                            ),
                            page('/staff', P.staffManage, <StaffPage />),
                            page('/staff/roles', P.staffManage, <RolesPage />),
                            page('/tenants', P.tenantsManage, <TenantsPage />),
                            page(
                                '/tenants/:id',
                                P.tenantsManage,
                                <TenantPage />,
                            ),
                            page(
                                '/notifications',
                                P.notificationsRead,
                                <NotificationsPage />,
                            ),
                            page(
                                '/settings/notifications',
                                P.notificationsRead,
                                <NotificationPreferencesPage />,
                            ),
                            page(
                                '/settings/templates',
                                P.templatesManage,
                                <TemplatesPage />,
                            ),
                            page(
                                '/settings/payment-systems',
                                P.systemsManage,
                                <PaymentSystemsPage />,
                            ),
                            page(
                                '/settings/payment-systems/:provider',
                                P.systemsManage,
                                <ProviderPage />,
                            ),
                            page(
                                '/settings/payment-systems/:provider/accounts/:account',
                                P.systemsManage,
                                <ProviderAccountPage />,
                            ),
                            page(
                                '/settings/push',
                                P.notificationsRead,
                                <PushPage />,
                            ),
                            {
                                path: '/settings/security',
                                element: <SecurityPage />,
                            },
                            { path: '/sandbox/*', element: <SandboxNotice /> },
                            { path: '*', element: <NotFound /> },
                        ],
                    },
                ],
            },
        ],
    },
];

export const createRouter = () => createBrowserRouter(routes);
export const createTestRouter = (entries: string[]) =>
    createMemoryRouter(routes, { initialEntries: entries });
